import {
  FeedbackIcon,
  GameSettingsIcon,
  ImprovementLogIcon,
  InsightsIcon,
  OverviewIcon,
  PlaytestsIcon,
  cn,
} from '@playerbase/ui';

import { useShell } from '../use-shell.js';
import { NavSection } from './nav-section.js';
import { SidebarCollapseToggle } from './sidebar-collapse-toggle.js';
import { SidebarNavButton } from './sidebar-nav-button.js';
import { StudioSwitcher } from './studio-switcher.js';
import { UserMenu } from './user-menu.js';
import { WanderingDood } from './wandering-dood.js';

type SidebarProps = {
  drawerOpen: boolean;
  collapsed: boolean;
  onToggleCollapsed: () => void;
  onClose: () => void;
};

// Primary sidebar. Flat leaves; per-section sub-items live on the
// secondary rail (SecondaryNav) so navigating into e.g. /playtests
// reveals the Playtest sessions + Playtest settings column to the right
// of this column.

export function Sidebar({
  drawerOpen,
  collapsed,
  onToggleCollapsed,
  onClose,
}: SidebarProps): JSX.Element {
  const { activeStudio, activeGame, feedbackHasUnread } = useShell();
  const studioBase = `/s/${activeStudio.shortId}`;
  const gameBase = activeGame !== null ? `${studioBase}/g/${activeGame.shortId}` : null;

  // The mobile drawer ignores collapsed: when the user has just opened
  // the drawer they want the full labelled experience, not a strip of
  // icons floating off-canvas.
  const effectiveCollapsed = drawerOpen ? false : collapsed;

  return (
    <>
      {/* Backdrop only renders on small screens when the drawer is open. */}
      <div
        onClick={onClose}
        aria-hidden
        className={cn(
          'fixed inset-0 z-40 bg-black/50 transition-opacity md:hidden',
          drawerOpen ? 'opacity-100' : 'pointer-events-none opacity-0',
        )}
      />
      <nav
        data-drawer-open={drawerOpen ? 'true' : 'false'}
        className={cn(
          'flex shrink-0 flex-col bg-surface border-r border-border',
          'transition-[width] duration-200',
          'fixed inset-y-0 left-0 z-50',
          'md:static md:translate-x-0',
          // Mobile drawer always renders the full-width labelled variant;
          // desktop honours the persisted collapsed flag via effectiveCollapsed.
          effectiveCollapsed ? 'w-14' : 'w-60',
          drawerOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0',
        )}
      >
        <SidebarCollapseToggle
          collapsed={collapsed}
          onToggle={onToggleCollapsed}
          onCloseDrawer={onClose}
        />

        <div
          className={cn(
            'flex-1 overflow-y-auto overflow-x-hidden',
            effectiveCollapsed ? 'flex flex-col items-center gap-1 px-2 py-2' : 'px-2 py-1',
          )}
        >
          {gameBase !== null ? (
            <>
              <SidebarNavButton
                to={`${gameBase}/overview`}
                icon={<OverviewIcon />}
                label="Overview"
                collapsed={effectiveCollapsed}
              />

              {effectiveCollapsed ? (
                <div aria-hidden className="my-1 h-px w-8 bg-border" />
              ) : (
                <NavSection>Research</NavSection>
              )}
              <SidebarNavButton
                to={`${gameBase}/insights`}
                icon={<InsightsIcon />}
                label="Insights"
                collapsed={effectiveCollapsed}
                matchAlso={[`${gameBase}/feedback-settings/insights`]}
              />
              <SidebarNavButton
                to={`${gameBase}/playtests`}
                icon={<PlaytestsIcon />}
                label="Playtests"
                collapsed={effectiveCollapsed}
                matchAlso={[`${gameBase}/playtest-settings`]}
              />
              <SidebarNavButton
                to={`${gameBase}/feedback`}
                icon={<FeedbackIcon />}
                label="Feedback"
                pulseDot={feedbackHasUnread}
                collapsed={effectiveCollapsed}
                // Listed specifically so /feedback-settings/insights
                // doesn't ALSO light up Feedback (it belongs to Insights).
                matchAlso={[
                  `${gameBase}/feedback-settings/watched-channels`,
                  `${gameBase}/feedback-settings/forms`,
                ]}
              />

              {effectiveCollapsed ? (
                <div aria-hidden className="my-1 h-px w-8 bg-border" />
              ) : (
                <NavSection>Actions</NavSection>
              )}
              <SidebarNavButton
                to={`${gameBase}/improvement-log`}
                icon={<ImprovementLogIcon />}
                label="Improvement log"
                collapsed={effectiveCollapsed}
              />

              {effectiveCollapsed ? (
                <div aria-hidden className="my-1 h-px w-8 bg-border" />
              ) : (
                <NavSection>Config</NavSection>
              )}
              <SidebarNavButton
                to={`${gameBase}/game-settings`}
                icon={<GameSettingsIcon />}
                label="Game settings"
                collapsed={effectiveCollapsed}
              />
            </>
          ) : (
            !effectiveCollapsed && (
              <p className="px-3 pt-3 text-xs leading-relaxed text-text-muted">
                Set up a game to unlock research, actions, and config.
              </p>
            )
          )}
        </div>

        <div className="mt-auto">
          {/* Dood lives on the keyline above the StudioSwitcher. The
              container shrinks with the sidebar; he just hops in a
              smaller box when collapsed. */}
          <WanderingDood />
          <div className="border-t border-border">
            <StudioSwitcher collapsed={effectiveCollapsed} />
          </div>
          <div className="border-t border-border">
            <UserMenu collapsed={effectiveCollapsed} />
          </div>
        </div>
      </nav>
    </>
  );
}
